import { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Languages,
  Volume2,
  Upload,
  Wand2,
  Search,
  Lock,
  BookOpen,
  Loader2,
  Settings
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { BeltIndicator } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import AudioRecorder from './AudioRecorder';
import VocabPointsSettings from './VocabPointsSettings';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Vocabulary categories
const VOCAB_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'greetings', label: 'Greetings' },
  { value: 'commands', label: 'Commands' },
  { value: 'counting', label: 'Counting' },
  { value: 'techniques', label: 'Techniques' },
  { value: 'body_parts', label: 'Body Parts' },
  { value: 'positions', label: 'Positions/Stances' },
  { value: 'etiquette', label: 'Etiquette' },
  { value: 'weapons', label: 'Weapons' },
  { value: 'directions', label: 'Directions' },
  { value: 'equipment', label: 'Equipment' },
];

// Difficulty levels
const DIFFICULTY_LEVELS = [
  { value: 1, label: 'Beginner (1)' },
  { value: 2, label: 'Elementary (2)' },
  { value: 3, label: 'Intermediate (3)' },
  { value: 4, label: 'Advanced (4)' },
  { value: 5, label: 'Expert (5)' },
];

// TTS Voice options (fallback if API fails)
// Note: Google's naming - Neural2-D is Male, Neural2-B/C are Female
const DEFAULT_TTS_VOICES = [
  { name: 'ja-JP-Neural2-D', gender: 'Male', type: 'Neural2', description: 'Male voice, clear and natural (recommended)' },
  { name: 'ja-JP-Neural2-B', gender: 'Female', type: 'Neural2', description: 'Female voice, clear and natural' },
  { name: 'ja-JP-Wavenet-C', gender: 'Male', type: 'WaveNet', description: 'Male voice, WaveNet quality' },
  { name: 'ja-JP-Wavenet-A', gender: 'Female', type: 'WaveNet', description: 'Female voice, WaveNet quality' },
];

// ============================================
// Vocabulary Form
// ============================================
const VocabularyForm = ({ term, programs, onSave, onCancel }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();
  const audioRef = useRef(null);

  const [formData, setFormData] = useState({
    japanese: term?.japanese || '',
    romaji: term?.romaji || '',
    english: term?.english || '',
    audio_url: term?.audio_url || '',
    audio_source: term?.audio_source || 'manual',
    category: term?.category || 'general',
    difficulty: term?.difficulty || 1,
    required_belt: term?.required_belt || '',
    required_program_ids: term?.required_program_ids || [],
    sort_order: term?.sort_order || 0,
    notes: term?.notes || '',
    is_active: term?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('ja-JP-Neural2-D');
  const [speakingRate, setSpeakingRate] = useState(0.9);
  const [pitch, setPitch] = useState(0);
  const [ttsTextSource, setTtsTextSource] = useState('romaji'); // 'romaji' or 'japanese'

  const handleProgramToggle = (programId) => {
    const current = formData.required_program_ids;
    if (current.includes(programId)) {
      setFormData({
        ...formData,
        required_program_ids: current.filter(id => id !== programId)
      });
    } else {
      setFormData({
        ...formData,
        required_program_ids: [...current, programId]
      });
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.url,
          audio_source: 'manual'
        });
        // Force audio element to reload with new source and auto-play
        if (audioRef.current) {
          audioRef.current.src = result.url;
          audioRef.current.load();
          // Auto-play after upload
          audioRef.current.oncanplaythrough = () => {
            audioRef.current.play();
            audioRef.current.oncanplaythrough = null;
          };
        }
      } else {
        alert('Failed to upload audio file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload audio file');
    } finally {
      setUploading(false);
    }
  };

  const handleRecordingComplete = async (audioBlob) => {
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      // Create a file from the blob with a proper name
      const fileName = `recording_${Date.now()}.wav`;
      const file = new File([audioBlob], fileName, { type: audioBlob.type });
      uploadFormData.append('file', file);

      const response = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.url,
          audio_source: 'recording'
        });
        // Force audio element to reload with new source and auto-play
        if (audioRef.current) {
          audioRef.current.src = result.url;
          audioRef.current.load();
          audioRef.current.oncanplaythrough = () => {
            audioRef.current.play();
            audioRef.current.oncanplaythrough = null;
          };
        }
      } else {
        alert('Failed to upload recorded audio');
      }
    } catch (error) {
      console.error('Recording upload error:', error);
      alert('Failed to upload recorded audio');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAudio = async () => {
    const textToSpeak = ttsTextSource === 'japanese' ? formData.japanese : formData.romaji;

    if (!textToSpeak) {
      alert(`Please enter ${ttsTextSource === 'japanese' ? 'Japanese' : 'Romaji'} text first`);
      return;
    }

    if (!term?.id) {
      alert('Please save the term first before generating audio');
      return;
    }

    const hadExistingAudio = !!formData.audio_url;
    setGeneratingAudio(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/vocabulary/${term.id}/generate-audio`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            voice_name: selectedVoice,
            speaking_rate: speakingRate,
            pitch: pitch,
            text: textToSpeak
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.audio_url,
          audio_source: 'google_tts'
        });

        // Auto-play the generated audio
        // Use a small timeout to ensure the audio element has the new URL
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.src = result.audio_url;
            audioRef.current.load();
            audioRef.current.oncanplaythrough = () => {
              audioRef.current.play().catch(() => {}); // Ignore autoplay restrictions
              audioRef.current.oncanplaythrough = null;
            };
          }
        }, 100);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to generate audio');
      }
    } catch (error) {
      console.error('TTS error:', error);
      alert('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current && formData.audio_url) {
      audioRef.current.play();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.japanese || !formData.romaji || !formData.english) {
      alert('Please fill in Japanese, Romaji, and English fields');
      return;
    }

    setSaving(true);
    try {
      const url = term?.id
        ? `${API_BASE}/api/admin/vocabulary/${term.id}`
        : `${API_BASE}/api/admin/vocabulary`;

      const payload = {
        ...formData,
        required_belt: formData.required_belt || null,
        sort_order: parseInt(formData.sort_order) || 0,
        difficulty: parseInt(formData.difficulty) || 1,
      };

      const response = await fetch(url, {
        method: term?.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const savedTerm = await response.json();
        onSave(savedTerm);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save vocabulary term');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save vocabulary term');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Japanese */}
            <div>
              <Label htmlFor="japanese">Japanese *</Label>
              <Input
                id="japanese"
                value={formData.japanese}
                onChange={(e) => setFormData({ ...formData, japanese: e.target.value })}
                placeholder="押忍 or おす"
                className="mt-1 text-2xl"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Kanji, Hiragana, or Katakana</p>
            </div>

            {/* Romaji */}
            <div>
              <Label htmlFor="romaji">Romaji *</Label>
              <Input
                id="romaji"
                value={formData.romaji}
                onChange={(e) => setFormData({ ...formData, romaji: e.target.value })}
                placeholder="osu"
                className="mt-1"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Romanized pronunciation</p>
            </div>

            {/* English */}
            <div>
              <Label htmlFor="english">English *</Label>
              <Input
                id="english"
                value={formData.english}
                onChange={(e) => setFormData({ ...formData, english: e.target.value })}
                placeholder="Respectful acknowledgment"
                className="mt-1"
                required
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
              >
                {VOCAB_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Difficulty */}
            <div>
              <Label htmlFor="difficulty">Difficulty</Label>
              <select
                id="difficulty"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
              >
                {DIFFICULTY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Audio Section */}
            <div className="md:col-span-3 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <Label className="font-medium">Audio Pronunciation</Label>
              {/* Always render audio element so it's available for auto-play */}
              <audio ref={audioRef} src={formData.audio_url || ''} />

              <div className="flex flex-wrap items-center gap-3 mt-2">
                {formData.audio_url && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={playAudio}
                    >
                      <Volume2 className="h-4 w-4 mr-2" />
                      Play Audio
                    </Button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Source: {formData.audio_source === 'google_tts' ? 'Google TTS' : formData.audio_source === 'recording' ? 'Recording' : 'Manual Upload'}
                    </span>
                  </>
                )}

                <div className="flex flex-wrap gap-2 items-center">
                  <AudioRecorder
                    onRecordingComplete={handleRecordingComplete}
                    disabled={uploading}
                  />

                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload Audio
                      </span>
                    </Button>
                  </label>

                  {term?.id && (
                    <div className="flex flex-col gap-3 w-full">
                      <div className="flex flex-wrap gap-2 items-center">
                        <select
                          value={selectedVoice}
                          onChange={(e) => setSelectedVoice(e.target.value)}
                          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                          title="Select TTS Voice"
                        >
                          {DEFAULT_TTS_VOICES.map(voice => (
                            <option key={voice.name} value={voice.name}>
                              {voice.gender} ({voice.type})
                            </option>
                          ))}
                        </select>
                        <select
                          value={ttsTextSource}
                          onChange={(e) => setTtsTextSource(e.target.value)}
                          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                          title="Text to speak"
                        >
                          <option value="romaji">Use Romaji</option>
                          <option value="japanese">Use Japanese</option>
                        </select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateAudio}
                          disabled={generatingAudio || !(ttsTextSource === 'japanese' ? formData.japanese : formData.romaji)}
                        >
                          {generatingAudio ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Wand2 className="h-4 w-4 mr-2" />
                          )}
                          Generate TTS
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <label className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Speed:</label>
                          <input
                            type="range"
                            min="0.5"
                            max="1.5"
                            step="0.1"
                            value={speakingRate}
                            onChange={(e) => setSpeakingRate(parseFloat(e.target.value))}
                            className="w-20"
                          />
                          <span className="text-gray-500 dark:text-gray-400 w-8">{speakingRate}x</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Pitch:</label>
                          <input
                            type="range"
                            min="-10"
                            max="10"
                            step="1"
                            value={pitch}
                            onChange={(e) => setPitch(parseFloat(e.target.value))}
                            className="w-20"
                          />
                          <span className="text-gray-500 dark:text-gray-400 w-8">{pitch > 0 ? '+' : ''}{pitch}</span>
                        </div>
                        {formData.category === 'commands' && (
                          <button
                            type="button"
                            onClick={() => { setSpeakingRate(1.4); setPitch(-6); }}
                            className="text-xs text-purple-600 hover:text-purple-800 underline"
                          >
                            Authoritative preset
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-3">
              <Label htmlFor="notes">Notes (optional)</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional context, usage examples, or tips..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
                rows={2}
              />
            </div>

            {/* Access Control Section */}
            <div className="md:col-span-3 border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30">
              <Label className="font-medium flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Access Control (Optional)
              </Label>
              <p className="text-sm text-gray-600 mb-3">
                Leave empty for all students, or select requirements to restrict access
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Belt Requirement */}
                <div>
                  <Label htmlFor="required_belt">Minimum Belt</Label>
                  <select
                    id="required_belt"
                    value={formData.required_belt}
                    onChange={(e) => setFormData({ ...formData, required_belt: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
                  >
                    <option value="">No Requirement</option>
                    {beltOptions.map(belt => (
                      <option key={belt.value} value={belt.value}>{belt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Program Requirements */}
                <div>
                  <Label>Required Programs</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {programs.map(program => (
                      <label
                        key={program.id}
                        className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer text-sm ${
                          formData.required_program_ids.includes(program.id)
                            ? 'bg-blue-100 border-blue-400'
                            : 'bg-background border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.required_program_ids.includes(program.id)}
                          onChange={() => handleProgramToggle(program.id)}
                          className="sr-only"
                        />
                        {program.title || program.name}
                      </label>
                    ))}
                  </div>
                  {programs.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">No programs available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

      {/* Form Actions */}
      <div className="flex gap-2 mt-6">
        <Button type="submit" disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {term?.id ? 'Update Term' : 'Create Term'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ============================================
// Vocabulary List Item
// ============================================
const VocabularyListItem = ({ term, programs, onEdit, onDelete, onPlayAudio }) => {
  const getProgramNames = () => {
    if (!term.required_program_ids?.length) return 'All Programs';
    return term.required_program_ids
      .map(id => {
        const program = programs.find(p => p.id === id);
        return program?.title || program?.name || id;
      })
      .join(', ');
  };

  const getCategoryLabel = () => {
    const cat = VOCAB_CATEGORIES.find(c => c.value === term.category);
    return cat?.label || term.category;
  };

  return (
    <div className={`flex items-center gap-4 p-3 bg-background rounded border hover:shadow-sm transition-shadow ${
      !term.is_active ? 'opacity-60' : ''
    }`}>
      {/* Japanese Character Display */}
      <div className="w-16 h-16 flex items-center justify-center bg-purple-50 rounded-lg flex-shrink-0">
        <span className="text-2xl font-bold text-purple-800">
          {term.japanese.slice(0, 2)}
        </span>
      </div>

      {/* Term Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-lg">{term.japanese}</span>
          <span className="text-gray-500 dark:text-gray-400">•</span>
          <span className="text-gray-600 dark:text-gray-400">{term.romaji}</span>
          {!term.is_active && (
            <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">Inactive</span>
          )}
        </div>
        <div className="text-gray-600 dark:text-gray-400 truncate">{term.english}</div>
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{getCategoryLabel()}</span>
          <span>Difficulty: {term.difficulty}/5</span>
          {term.required_belt && (
            <span className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <BeltIndicator belt={term.required_belt} stripes={0} />
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {term.audio_url && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPlayAudio(term)}
            title="Play Audio"
          >
            <Volume2 className="h-4 w-4 text-purple-600" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onEdit(term)}>
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(term)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// Main Vocabulary Manager Component
// ============================================
const VocabularyManager = () => {
  const [terms, setTerms] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [termsRes, programsRes, categoriesRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/vocabulary`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/api/programs`),
        fetch(`${API_BASE}/api/admin/vocabulary/categories`, { headers: getAuthHeaders() })
      ]);

      if (termsRes.ok) {
        const data = await termsRes.json();
        setTerms(data);
      }

      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    fetchData();
    setShowForm(false);
    setEditingTerm(null);
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Delete "${term.japanese}" (${term.english})?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/vocabulary/${term.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
      } else {
        alert('Failed to delete term');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete term');
    }
  };

  const handlePlayAudio = (term) => {
    if (term.audio_url) {
      if (audioRef.current) {
        audioRef.current.src = term.audio_url;
        audioRef.current.play();
      }
    }
  };

  // Filter terms
  const filteredTerms = terms.filter(term => {
    const matchesSearch = !searchTerm ||
      term.japanese.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.romaji.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.english.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || term.category === categoryFilter;
    const matchesDifficulty = !difficultyFilter || term.difficulty === parseInt(difficultyFilter);

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Group terms by category for display
  const termsByCategory = filteredTerms.reduce((acc, term) => {
    const cat = term.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(term);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} />

      <PageTitle
        title="Japanese Vocabulary"
        description={`${terms.length} terms • Students can practice with flash cards`}
        icon={<Languages className="h-6 w-6" />}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} title="Points Configuration">
              <Settings className="h-4 w-4" />
            </Button>
            <Button onClick={() => { setEditingTerm(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Term
            </Button>
          </div>
        }
      />

      {/* Points Settings Modal */}
      <VocabPointsSettings open={showSettings} onClose={() => setShowSettings(false)} />

      {/* Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditingTerm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-purple-600" />
              {editingTerm?.id ? 'Edit Vocabulary Term' : 'Add New Term'}
            </DialogTitle>
          </DialogHeader>
          <VocabularyForm
            term={editingTerm}
            programs={programs}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingTerm(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Japanese, Romaji, or English..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
            >
              <option value="">All Categories</option>
              {VOCAB_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
            >
              <option value="">All Difficulties</option>
              {DIFFICULTY_LEVELS.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Terms List */}
      {Object.keys(termsByCategory).length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No vocabulary terms yet</h3>
            <p className="text-gray-600 mb-4">
              Add Japanese vocabulary terms for students to learn with flash cards.
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Term
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(termsByCategory).map(([category, categoryTerms]) => {
          const categoryLabel = VOCAB_CATEGORIES.find(c => c.value === category)?.label || category;
          return (
            <div key={category}>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                {categoryLabel}
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{categoryTerms.length}</span>
              </h3>
              <div className="space-y-2">
                {categoryTerms.map(term => (
                  <VocabularyListItem
                    key={term.id}
                    term={term}
                    programs={programs}
                    onEdit={(t) => { setEditingTerm(t); setShowForm(true); }}
                    onDelete={handleDelete}
                    onPlayAudio={handlePlayAudio}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default VocabularyManager;
